import { createFileRoute, Link } from "@tanstack/react-router";
import { useReveal } from "@/hooks/use-reveal";
import { TldrBox } from "@/components/tldr-box";
import { PageHero } from "@/components/page-hero";
import { ReviewsProof } from "@/components/reviews-proof";
import { BAR_PARFUM_OPEN_EVENT } from "@/lib/bar-parfum-promo";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";
import barHero from "@/assets/bar-parfum-atelier.jpg";
import { BarParfumFunnel } from "@/components/bar-parfum-funnel";
import imgReception from "@/assets/parfum-bar-reception.jpg.asset.json";
import imgMaries from "@/assets/parfum-maries-atelier.jpg.asset.json";
import imgInitiales from "@/assets/parfum-flacon-initiales.jpg.asset.json";
import imgFlacons from "@/assets/parfum-flacons.jpg.asset.json";
import imgBarMariage from "@/assets/parfum-bar-mariage.jpg.asset.json";
import imgMerci from "@/assets/parfum-flacon-merci.jpg.asset.json";
import imgProvence from "@/assets/parfum-flacon-provence.jpg.asset.json";
import imgCouple from "@/assets/parfum-flacon-maries.jpg.asset.json";

const CARD_IMAGES = [imgReception.url, imgMaries.url, imgInitiales.url, imgBarMariage.url];
const GALLERY = [
  { src: imgBarMariage.url, alt: "Bar à parfum fleuri pendant un cocktail de mariage" },
  { src: imgCouple.url, alt: "Flacon personnalisé aux prénoms des mariés devant un château" },
  { src: imgMaries.url, alt: "Les mariés composent leur parfum à l'atelier" },
  { src: imgProvence.url, alt: "Flacon « Un été en Provence » au milieu des lavandes" },
  { src: imgFlacons.url, alt: "Flacons de parfum élégants" },
  { src: imgMerci.url, alt: "Flacon de remerciement personnalisé pour un événement d'entreprise" },
];

export const Route = createFileRoute("/bar-a-parfum")({
  head: () => ({
    meta: [
      {
        title: "Bar à parfum mariage & événement Provence | DJ Val",
      },
      {
        name: "description",
        content: metaDesc(
          "Bar à parfum pour mariage et événement en Provence : animatrice dédiée, parfum choisi par chaque invité, flacon personnalisé à emporter. Formules dès 12 €/invité, minimum 50 personnes. Nouveauté saison 2027."
        ),
      },
      {
        property: "og:title",
        content: "Bar à parfum Provence — l'animation olfactive de vos réceptions | DJ Val",
      },
      {
        property: "og:description",
        content: metaDesc(
          "Une animatrice dédiée, un parfum créé par chaque invité, un flacon personnalisé à emporter. Mariages et événements en Vaucluse, Avignon et Aix. Dès 12 €/invité."
        ),
      },
      { property: "og:url", content: "https://dj-val.fr/bar-a-parfum" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/bar-a-parfum" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": "https://dj-val.fr/bar-a-parfum/#service",
              name: "Bar à parfum — animation olfactive mariage & événement",
              serviceType: "Animation événementielle — bar à parfum",
              provider: { "@id": "https://dj-val.fr/#business" },
              areaServed: [
                "Vaucluse", "Bouches-du-Rhône", "Gard",
                "Cavaillon", "Avignon", "Aix-en-Provence",
                "Salon-de-Provence", "Pertuis", "Carpentras", "Marseille",
              ].map((name) => ({ "@type": "City", name })),
              description:
                "Animation bar à parfum animée par une professionnelle : chaque invité compose son parfum et repart avec un flacon personnalisé. Pour mariages, cocktails, événements privés et d'entreprise en Provence. Minimum 50 personnes.",
              offers: {
                "@type": "AggregateOffer",
                priceCurrency: "EUR",
                lowPrice: "12",
                highPrice: "17",
                offerCount: "3",
                availability: "https://schema.org/InStock",
                url: "https://dj-val.fr/bar-a-parfum",
                shippingDetails: {
                  "@type": "OfferShippingDetails",
                  shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "EUR" },
                  shippingDestination: { "@type": "DefinedRegion", name: "Vaucluse, Provence" },
                  deliveryTime: {
                    "@type": "ShippingDeliveryTime",
                    handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
                  },
                },
                hasMerchantReturnPolicy: {
                  "@type": "MerchantReturnPolicy",
                  applicableCountry: "FR",
                  returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
                  merchantReturnLink: "https://dj-val.fr/conditions-de-location",
                },
              },
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "Combien coûte le bar à parfum pour un mariage ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Trois formules par invité : Essentielle 12 €, Signature 14 € et Prestige 17 €. Le minimum est de 50 personnes, soit un forfait de 600 €. Devis personnalisé sous 24 h selon la date, le lieu et le nombre d'invités.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Pour quels événements le bar à parfum est-il adapté ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Mariages (cocktail et réception), anniversaires, soirées d'entreprise, séminaires, gender reveal et soirées thématiques. L'animation s'installe en 30 minutes et fonctionne aussi bien en intérieur qu'en extérieur.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Comment se déroule l'animation du bar à parfum ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Une animatrice dédiée installe le bar, guide chaque invité dans la création de son parfum (essences, dosages, tests) et personnalise l'étiquette de son flacon. Les invités repartent avec leur parfum. L'animation couvre généralement le cocktail et le début de réception.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Pourquoi un minimum de 50 personnes ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Le bar à parfum est pensé pour des réceptions : l'animatrice et le matériel sont calibrés pour accueillir au moins 50 invités, soit un forfait minimum de 600 €. Au-delà, le prix reste par invité.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Quelle est l'offre de lancement pour la saison 2027 ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Les 5 premiers mariages réservés pour la saison 2027 reçoivent l'étiquetage des flacons aux prénoms des mariés offert. Demande possible via le formulaire ou le devis.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Dans quelles zones le bar à parfum est-il proposé ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Cavaillon, Avignon, Aix-en-Provence, Salon-de-Provence, Pertuis, Carpentras et tout le Vaucluse, ainsi que la Provence (Bouches-du-Rhône, Gard). Déplacement inclus dans un rayon de 80 km autour de Cavaillon.",
                  },
                },
              ],
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
                { "@type": "ListItem", position: 2, name: "Prestations", item: "https://dj-val.fr/soirees-evenements" },
                { "@type": "ListItem", position: 3, name: "Bar à parfum", item: "https://dj-val.fr/bar-a-parfum" },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const FORMULES = [
  {
    name: "Essentielle",
    price: "12 €",
    sub: "Par invité · min. 50 personnes",
    inc: [
      "Animatrice dédiée pendant le cocktail",
      "Création du parfum par chaque invité",
      "Flacon 15 ml personnalisé à emporter",
      "Installation & rangement incluses",
    ],
    cta: "L'animation simple et efficace pour tous vos invités",
  },
  {
    name: "Signature",
    price: "14 €",
    sub: "Par invité · min. 50 personnes",
    highlight: true,
    inc: [
      "Tout l'essentiel",
      "Sélection d'essences élargie (Provence)",
      "Flacon 30 ml + étiquette personnalisée",
      "Conseils d'accords et atelier guidé",
      "Étiquettes aux prénoms des mariés offertes pour les 5 premiers mariages",
    ],
    cta: "La formule la plus demandée pour les mariages",
  },
  {
    name: "Prestige",
    price: "17 €",
    sub: "Par invité · min. 50 personnes",
    inc: [
      "Tout Signature",
      "Parfumeur·se présent en continu",
      "Flacon 50 ml rechargeable",
      "Coffret cadeau & encart remerciement",
      "Personnalisation complète aux couleurs de l'événement",
    ],
    cta: "Pour une réception haute couture",
  },
];

const ETAPES = [
  { n: "01", t: "Brief & réservation", d: "Date, lieu, nombre d'invités et formule. Les 5 premiers mariages 2027 réservent l'étiquetage personnalisé offert." },
  { n: "02", t: "Installation", d: "Le bar s'installe en 30 minutes, en intérieur comme en extérieur, sans empiéter sur le plan de traiteur." },
  { n: "03", t: "Atelier parfum", d: "Chaque invité choisit ses essences, teste, compose — guidé par l'animatrice, avec la musique du cocktail en fond." },
  { n: "04", t: "Souvenir à emporter", d: "Flacon personnalisé à l'effet des mariés ou de l'événement : le cadeau que les invités gardent." },
];

const FAQ = [
  {
    q: "Combien coûte le bar à parfum pour un mariage ?",
    a: "Trois formules par invité : Essentielle 12 €, Signature 14 € et Prestige 17 €. Le minimum est de 50 personnes, soit un forfait de 600 €. Devis personnalisé sous 24 h selon la date, le lieu et le nombre d'invités.",
  },
  {
    q: "Pour quels événements le bar à parfum est-il adapté ?",
    a: "Mariages (cocktail et réception), anniversaires, soirées d'entreprise, séminaires, gender reveal et soirées thématiques. L'animation s'installe en 30 minutes et fonctionne aussi bien en intérieur qu'en extérieur.",
  },
  {
    q: "Comment se déroule l'animation ?",
    a: "Une animatrice dédiée installe le bar, guide chaque invité dans la création de son parfum (essences, dosages, tests) et personnalise l'étiquette de son flacon. Les invités repartent avec leur parfum.",
  },
  {
    q: "Pourquoi un minimum de 50 personnes ?",
    a: "Le bar à parfum est pensé pour des réceptions : l'animatrice et le matériel sont calibrés pour accueillir au moins 50 invités, soit un forfait minimum de 600 €.",
  },
  {
    q: "Quelle est l'offre de lancement saison 2027 ?",
    a: "Les 5 premiers mariages réservés pour la saison 2027 reçoivent l'étiquetage des flacons aux prénoms des mariés offert.",
  },
  {
    q: "Dans quelles zones intervenez-vous ?",
    a: "Cavaillon, Avignon, Aix-en-Provence, Salon-de-Provence, Pertuis, Carpentras et tout le Vaucluse, plus la Provence (Bouches-du-Rhône, Gard). 80 km autour de Cavaillon inclus.",
  },
];

function Page() {
  useReveal();

  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={barHero}
        eyebrow="Nouveauté · Saison 2027"
        title={<><span className="italic">Bar à parfum</span><span className="block">l'animation qui se souvient</span></>}
        subtitle="Une animatrice dédiée, un parfum composé par chaque invité, un flacon personnalisé à emporter. L'animation olfactive de vos mariages et événements en Provence, dès 12 €/invité."
        primary={{ label: "Réserver mon bar à parfum", href: "#reserver" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:0767908667" }}
      />
      <ReviewsProof />
      <BarParfumFunnel />

      {/* TLDR */}
      <div className="px-6">
        <TldrBox
          title="En bref"
          summary="DJ Val propose le bar à parfum de L'Atelier Olfactif pour les mariages et événements en Provence : animatrice dédiée, atelier de création olfactive pendant le cocktail, flacon personnalisé à emporter. Trois formules de 12 € à 17 € par invité, minimum 50 personnes (600 €). Les 5 premiers mariages 2027 reçoivent l'étiquetage aux prénoms des mariés offert."
          items={[
            { label: "Formules", value: "12 € · 14 € · 17 € par invité" },
            { label: "Minimum", value: "50 personnes · 600 €" },
            { label: "Inclus", value: "Animatrice dédiée · flacon personnalisé" },
            { label: "Zone", value: "Cavaillon, Avignon, Aix, Vaucluse" },
          ]}
        />
      </div>

      {/* POURQUOI */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-foreground">L'animation olfactive</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Un souvenir que vos invités <span className="italic">emportent chez eux</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
          <p className="max-w-2xl mx-auto mt-6 text-sm leading-relaxed font-light">
            Pendant le cocktail, pendant que le DJ assure la transition, le bar à parfum capte vos invités :
            chacun compose son parfum, le teste, le personnalise — et repart avec son flacon gravé à votre nom.
          </p>
        </div>
        <div className="grid gap-8 sm:grid-cols-2">
          {[
            {
              title: "Pendant que le DJ fait le son",
              text: "L'animation tourne en parallèle du cocktail et du dîner : zéro temps mort, zéro file d'attente au bar. L'animatrice gère le flux, moi je gère la piste.",
            },
            {
              title: "Animatrice dédiée",
              text: "Une professionnelle de l'atelier encadre l'atelier olfactive de bout en bout : conseil des essences, dosages, finitions et étiquettes.",
            },
            {
              title: "Flacon personnalisé",
              text: "Étiquette à l'effet des mariés ou de l'événement. Offert pour les 5 premiers mariages de la saison 2027.",
            },
            {
              title: "Intérieur comme extérieur",
              text: "Bastide aixoise, mas du Luberon, salle de réception ou jardin : le bar s'installe en 30 minutes sans câble ni contrainte technique.",
            },
          ].map((e, i) => (
            <div
              key={e.title}
              className="border border-foreground/10 overflow-hidden sr"
              data-reveal
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <img src={CARD_IMAGES[i]} alt={e.title} loading="lazy" className="h-56 w-full object-cover" />
              <div className="p-8">
              <p className="font-serif-display italic text-foreground text-xl">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="font-serif-display text-2xl text-foreground mt-3">{e.title}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm leading-relaxed font-light">{e.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* GALERIE */}
      <section className="pb-24 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-10 sr" data-reveal>
          <p className="eyebrow text-foreground">En images</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            L'expérience <span className="italic">L'Atelier Olfactif</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {GALLERY.map((g, i) => (
            <img key={g.src} src={g.src} alt={g.alt} loading="lazy" className={`w-full object-cover rounded ${i === 0 ? "col-span-2 h-64 md:h-full md:row-span-2" : "h-48 md:h-64"}`} />
          ))}
        </div>
        <div className="mt-10 text-center">
          <a href="#reserver" className="inline-block bg-[var(--gold)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110">Je veux ça pour mon événement</a>
        </div>
      </section>

      {/* FORMULES */}
      <section className="py-24 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 sr" data-reveal>
            <p className="eyebrow text-foreground">3 formules</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
              Choisissez votre <span className="italic">formule parfum</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
            <p className="mt-6 text-sm font-light">Minimum 50 personnes · Forfait 600 € · Devis sous 24 h</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {FORMULES.map((f, i) => (
              <div
                key={f.name}
                className={`border p-8 flex flex-col sr ${
                  f.highlight
                    ? "border-[var(--gold)] bg-[var(--gold)]/5"
                    : "border-foreground/10"
                }`}
                data-reveal
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                {f.highlight && (
                  <p className="eyebrow text-[var(--gold)] mb-3">La plus demandée</p>
                )}
                <h3 className="font-serif-display text-2xl text-foreground">{f.name}</h3>
                <p className="font-serif-display italic gold-text-shine text-4xl mt-3">
                  {f.price}
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-2">
                  {f.sub}
                </p>
                <span className="gold-rule !w-8 my-6" />
                <ul className="space-y-2 text-sm font-light flex-1">
                  {f.inc.map((it) => (
                    <li key={it} className="flex gap-2">
                      <span className="text-[var(--gold)]">·</span>
                      {it}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs text-muted-foreground italic">{f.cta}</p>
                <a
                  href="#reserver"
                  className="mt-6 block text-center bg-[var(--gold)] text-foreground py-3 text-[10px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
                >
                  Réserver cette formule
                </a>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm font-light text-muted-foreground sr" data-reveal>
            Combinaison avec un DJ Val : demandez le duo DJ + bar à parfum sur le formulaire de devis,
            l'animation se synchronise avec vos temps forts.
          </p>
        </div>
      </section>

      {/* OFFRE 5 PREMIERS */}
      <section className="py-24 px-6 max-w-4xl mx-auto text-center">
        <div className="sr" data-reveal>
          <p className="eyebrow text-foreground">Offre de lancement</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Les 5 premiers mariages 2027 : <span className="italic gold-text-shine">étiquetage offert</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm font-light leading-relaxed">
            Réservez votre date parmi les 5 premiers mariages de la saison 2027 et les étiquettes
            des flacons personnalisées aux prénoms des mariés vous sont offertes — sur toutes les formules.
          </p>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event(BAR_PARFUM_OPEN_EVENT))}
            className="mt-8 bg-[var(--gold)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
          >
            Je réserve ma date
          </button>
        </div>
      </section>

      {/* METHODE */}
      <section className="py-24 px-6 max-w-5xl mx-auto bg-card border-y border-foreground/10">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-foreground">Comment ça se passe</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Du brief au <span className="italic">flacon emporté</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-px bg-foreground/10 border border-foreground/10">
          {ETAPES.map((p, i) => (
            <div
              key={p.t}
              className="bg-background p-8 grid md:grid-cols-[80px_1fr] gap-6 sr"
              data-reveal
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <p className="font-serif-display italic text-[var(--gold)] text-3xl">{p.n}</p>
              <div>
                <h3 className="font-serif-display text-xl text-foreground">{p.t}</h3>
                <p className="text-sm leading-relaxed font-light mt-2">{p.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 max-w-3xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-foreground">FAQ</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            Questions <span className="italic">fréquentes</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {FAQ.map((f) => (
            <details
              key={f.q}
              className="border border-foreground/10 p-6 group [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="cursor-pointer flex justify-between items-start gap-4 text-foreground font-serif-display text-lg">
                {f.q}
                <span className="text-[var(--gold)] text-2xl leading-none group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <p className="mt-4 text-sm font-light leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CROSS-LINKS */}
      <section className="py-16 px-6 bg-card border-t border-foreground/10">
        <div className="max-w-4xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-foreground">À combiner avec</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/dj-mariage"
              className="border border-foreground/15 px-5 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-foreground"
            >
              DJ Mariage
            </Link>
            <Link
              to="/soirees-evenements"
              className="border border-foreground/15 px-5 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-foreground"
            >
              Soirées & événements
            </Link>
            <Link
              to="/tarifs"
              className="border border-foreground/15 px-5 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-foreground"
            >
              Tarifs & formules
            </Link>
            <Link
              to="/calculateur"
              className="border border-foreground/15 px-5 py-2 text-xs uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-foreground"
            >
              Calculateur de devis
            </Link>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-4xl text-foreground">
            Réservez votre <span className="italic gold-text-shine">date 2027</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm font-light leading-relaxed">
            Les 5 premières réservations de la saison 2027 gagnent l'étiquetage personnalisé offert.
            Dites-nous date, lieu et nombre d'invités — devis ferme sous 24 h.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a
              href="#reserver"
              className="bg-[var(--gold)] text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Demander mon devis
            </a>
            <a
              href="tel:0767908667"
              onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "bar_a_parfum" })}
              className="border border-foreground/20 text-foreground px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-foreground transition-colors"
            >
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
